import React, {useCallback, useEffect} from 'react';
import Select from "react-select";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import usePrevious from "v4/hooks/usePrevious";

export default function CustomReactSelect({
                                              name,
                                              value,
                                              options,
                                              isMulti = false,
                                              isReadOnly = false,
                                              placeholder,
                                              onChange,
                                              htmlConstraints,
                                              selectParams = {}
                                          }) {
    const {t} = usePolTranslation();

    const handleChange = (option) => {
        if (option) {
            onChange(isMulti ? option.map((item) => item.value) : option.value);
        } else {
            onChange(isMulti ? [] : '');
        }
    }

    const getValue = useCallback((val) => {
        if (options && val) {
            return isMulti
                ? options.filter(option => val.indexOf(String(option?.value)) >= 0)
                : options.find(option => option?.value === val);
        }

        return isMulti ? [] : "";
    }, [isMulti, options]);

    const previousOptions = usePrevious(options);
    useEffect(() => {
        if (previousOptions === undefined || JSON.stringify(previousOptions) === JSON.stringify(options)) return;
        handleChange(getValue(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    let isReadOnlyParams = {};
    if (isReadOnly) {
        isReadOnlyParams = {
            isClearable: false,
            isSearchable: false,
            openMenuOnClick: false,
            menuIsOpen: false,
        }
    }

    const {onChange: onChangeSelectParams, ...restSelectParams} = selectParams ?? {};

    return (
        <Select name={name}
                {...htmlConstraints}
                value={getValue(value)}
                onChange={onChangeSelectParams ? data => onChangeSelectParams(data, handleChange) : handleChange}
                options={getOptionsWithGroup(options)}
                isMulti={isMulti}
                placeholder={placeholder ?? t('select')}
                noOptionsMessage={() => t('no_results')}
                closeMenuOnSelect={!isMulti}
                isClearable={true}
                menuPlacement="auto"
                className={`react-select ${htmlConstraints?.required ? "select-is-required" : ""}`}
                classNamePrefix="react-select"
                onFocus={({target}) => target.closest('.react-select').dataset.focus = "true"}
                onBlur={({target}) => target.closest('.react-select').dataset.focus = "false"}
                styles={{...selectStyle, ...(restSelectParams.overrideStyles && {...restSelectParams.overrideStyles(selectStyle)})}}
                {...restSelectParams}
                {...isReadOnlyParams}
        />
    )
}

function getOptionsWithGroup(options) {
    const groups = options.reduce((acc, option) => {
        const group = option.group || 'default';

        if (!acc[group]) acc[group] = [];

        acc[group].push(option);

        return acc;
    }, {});

    if (Object.keys(groups).length === 1 && groups.default) {
        return groups.default;
    }

    return Object.entries(groups).map(([group, opts]) => ({
        label: group === 'default' ? '' : group,
        options: opts,
    }));
}

export const selectStyle = {
    control: (base) => ({
        ...base,
        boxShadow: 'none',
        border: '1px solid transparent',
        backgroundColor: '#f3f1fc',
        fontFamily: 'inherit',
        outline: 'none',
        cursor: 'pointer',
        width: '100%',
        minHeight: '3em',
        ':hover': {
            border: '1px solid transparent',
        },
    }),
    container: (base) => ({
        ...base,
        width: '100%',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        ':hover': {color: '#7565c0'},
        color: '#7565c0',
        padding: '0.5em',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#a49bd6',
    }),
    valueContainer: (base) => ({
        ...base,
        textOverflow: "ellipsis",
        maxWidth: "80%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        paddingBlock: "0.2em",
    }),
    singleValue: (base) => ({
        ...base,
        color: '#7565c0',
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: getOptionBackground(state),
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        color: getOptionTextColor(state),
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: '0.5em',
    }),
    indicatorSeparator: (base) => ({
        ...base,
        marginBlock: '0.5em',
    }),
}

function getOptionBackground(state) {
    const isDisabledNotSelected = state.isDisabled && !state.isSelected;
    const isActiveOrSelected = state.isFocused || state.isSelected;

    if (isDisabledNotSelected) return 'rgba(0,0,0,0.05)';

    if (isActiveOrSelected) return '#a59bd6';

    return 'transparent';
}

function getOptionTextColor(state) {
    const isDisabledNotSelected = state.isDisabled && !state.isSelected;
    const isActiveOrSelected = state.isFocused || state.isSelected;

    if (isDisabledNotSelected) return 'gray';

    if (isActiveOrSelected) return 'white';

    return 'black';
}
